import { resolveOnboardingRole } from './roleUtils';

const getTourStepDefinition = ({
  context = {},
  id,
  label,
  setActiveTab,
  setSettingsTab
}) => {
  const definitions = {
    stock: {
      navSelector: '#tab-stock',
      selector: '[data-tour-id="stock-overview-panel"]',
      title: label || 'Stock',
      description: Number(context.lowStockCount || 0) > 0
        ? 'This tab shows current stock and already highlights low or critical items.'
        : 'This tab shows current stock levels, summaries, and filterable inventory views.',
      prep: () => setActiveTab('stock')
    },
    insights: {
      navSelector: '#tab-insights',
      selector: '[data-tour-id="insights-overview"]',
      title: label || 'Insights',
      description: 'Use insights to review movement trends, dead stock, reorder signals, and order performance.',
      prep: () => setActiveTab('insights')
    },
    add: {
      navSelector: '#tab-add',
      selector: '[data-tour-id="add-movement-form"]',
      title: label || 'Add Movement',
      description: 'This form records inbound, outbound, and hold movements that drive stock levels.',
      prep: () => setActiveTab('add')
    },
    transactions: {
      navSelector: '#tab-transactions',
      selector: '[data-tour-id="movement-history-table"]',
      title: label || 'Movement History',
      description: 'Review the transaction ledger here to verify movements, filters, and corrections.',
      prep: () => setActiveTab('transactions')
    },
    raw_materials: {
      navSelector: '#tab-raw_materials',
      selector: '[data-tour-id="raw-materials-settings"]',
      title: label || 'Raw Materials',
      description: 'Define your raw material types and characteristics here before products and orders rely on them.',
      prep: () => {
        setActiveTab('settings');
        setSettingsTab('rawMaterials');
      }
    },
    product_add: {
      navSelector: '#tab-product_add',
      selector: '[data-tour-id="product-bom-builder"]',
      title: label || 'Add Product',
      description: 'Build products here and map the bill of materials required for each unit.',
      prep: () => setActiveTab('product_add')
    },
    products: {
      navSelector: '#tab-products',
      selector: '[data-tour-id="products-overview"]',
      title: label || 'View Products',
      description: 'Use this tab to browse products, inspect BOM coverage, and review product details.',
      prep: () => setActiveTab('products')
    },
    order: {
      navSelector: '#tab-order',
      selector: '[data-tour-id="create-order-form"]',
      title: label || 'Create Order',
      description: 'Create orders here by selecting products, delivery dates, and the quantities required.',
      prep: () => setActiveTab('order')
    },
    orders: {
      navSelector: '#tab-orders',
      selector: '[data-tour-id="orders-overview"]',
      title: label || 'View Orders',
      description: 'This tab lists all orders so you can track status, holds, costs, and timelines.',
      prep: () => setActiveTab('orders')
    },
    pipeline_board: {
      navSelector: '#tab-pipeline_board',
      selector: '[data-tour-id="workflow-board"]',
      title: label || 'Workflow',
      description: 'Move work through stages here and monitor overdue, blocked, or unassigned orders.',
      prep: () => setActiveTab('pipeline_board')
    },
    staff: {
      navSelector: '#tab-staff',
      selector: '[data-tour-id="staff-overview"]',
      title: label || 'Staff',
      description: 'Manage staff records, attendance, and key employee details in this section.',
      prep: () => setActiveTab('staff')
    },
    customers: {
      navSelector: '#tab-customers',
      selector: '[data-tour-id="customers-overview"]',
      title: label || 'Customers',
      description: 'Store and manage customer profiles here using your configured customer schema.',
      prep: () => setActiveTab('customers')
    },
    contractors: {
      navSelector: '#tab-contractors',
      selector: '[data-tour-id="contractors-overview"]',
      title: label || 'Contractors',
      description: 'Use this section to manage contractor profiles and related custom records.',
      prep: () => setActiveTab('contractors')
    },
    settings_system: {
      navSelector: '#tab-settings_system',
      selector: '[data-tour-id="system-settings"]',
      title: label || 'System Settings',
      description: 'Tune thresholds, pipeline stages, defaults, and other company-wide operating settings here.',
      prep: () => {
        setActiveTab('settings');
        setSettingsTab('system');
      }
    },
    settings_lists: {
      navSelector: '#tab-settings_lists',
      selector: '[data-tour-id="manage-lists-settings"]',
      title: label || 'Manage Lists',
      description: 'Manage reusable dropdown values here so forms across the app stay consistent.',
      prep: () => {
        setActiveTab('settings');
        setSettingsTab('customLists');
      }
    },
    settings_users: {
      navSelector: '#tab-settings_users',
      selector: '[data-tour-id="user-access-table"]',
      title: label || 'User Access',
      description: 'Create users, change passwords, and control role-based access in this section.',
      prep: () => {
        setActiveTab('settings');
        setSettingsTab('users');
      }
    },
    help: {
      navSelector: '#tab-help',
      selector: '.header-help-btn',
      title: label || 'Help',
      description: 'Use Help anytime to restart this guided tour.',
      prep: null
    }
  };

  if (definitions[id]) return definitions[id];
  return {
    navSelector: `#tab-${id}`,
    selector: `[data-tour-id="custom-form-${id}"]`,
    title: label || id,
    description: `${label || 'This tab'} is a company-specific form or workflow area available in this workspace.`,
    prep: () => setActiveTab(id)
  };
};

const buildTourItemsInNavbarOrder = ({
  isAdminLike,
  orderedTabs,
  ordersEnabled,
  visibleTabIds
}) => {
  const items = [];
  if (visibleTabIds.has('stock')) items.push({ id: 'stock', label: 'Stock' });
  if (visibleTabIds.has('insights')) items.push({ id: 'insights', label: 'Insights' });
  if (visibleTabIds.has('add')) items.push({ id: 'add', label: 'Add Movement' });
  if (visibleTabIds.has('transactions')) items.push({ id: 'transactions', label: 'Movement History' });
  if (visibleTabIds.has('settings')) items.push({ id: 'raw_materials', label: 'Raw Materials' });
  if (visibleTabIds.has('product_add')) items.push({ id: 'product_add', label: 'Add Product' });
  if (visibleTabIds.has('products')) items.push({ id: 'products', label: 'View Products' });
  if (ordersEnabled && visibleTabIds.has('order')) items.push({ id: 'order', label: 'Create Order' });
  if (ordersEnabled && visibleTabIds.has('orders')) items.push({ id: 'orders', label: 'View Orders' });
  if (ordersEnabled && visibleTabIds.has('pipeline_board')) items.push({ id: 'pipeline_board', label: 'Workflow' });

  (orderedTabs || [])
    .filter((tab) => tab.mode === 'custom_form' && visibleTabIds.has(tab.id))
    .forEach((tab) => {
      items.push({ id: tab.id, label: tab.label || tab.id });
    });

  if (visibleTabIds.has('staff')) items.push({ id: 'staff', label: 'Staff' });
  if (visibleTabIds.has('customers')) items.push({ id: 'customers', label: 'Customers' });
  if (visibleTabIds.has('contractors')) items.push({ id: 'contractors', label: 'Contractors' });
  if (visibleTabIds.has('settings')) items.push({ id: 'settings_system', label: 'System Settings' });
  if (visibleTabIds.has('settings')) items.push({ id: 'settings_lists', label: 'Manage Lists' });
  if (isAdminLike && visibleTabIds.has('settings')) items.push({ id: 'settings_users', label: 'User Access' });
  items.push({ id: 'help', label: 'Help' });
  return items;
};

export const buildDashboardTourSteps = ({
  context = {},
  orderedTabs = [],
  ordersEnabled,
  role,
  setActiveTab,
  setSettingsTab,
  visibleTabIds
}) => {
  const roleKey = resolveOnboardingRole(role);
  const isAdminLike = roleKey === 'owner' || roleKey === 'admin';

  return buildTourItemsInNavbarOrder({
    isAdminLike,
    orderedTabs,
    ordersEnabled,
    visibleTabIds
  }).map((item) => ({
    id: item.id,
    ...getTourStepDefinition({
      context,
      id: item.id,
      label: item.label,
      setActiveTab,
      setSettingsTab
    })
  }));
};
